import React from 'react';
import { Shield, Lock, Eye, FileText, CheckCircle2, Mail, Phone, MapPin } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div style={{ background: '#F8FAF7', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", paddingBottom: '4rem' }}>
      {/* Header Banner */}
      <div style={{
        background: '#084734',
        color: 'white',
        padding: '4rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(206, 241, 123, 0.08)',
          pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(206, 241, 123, 0.15)',
            border: '1px solid rgba(206, 241, 123, 0.3)',
            padding: '6px 16px',
            borderRadius: '50px',
            color: '#CEF17B',
            fontSize: '0.85rem',
            fontWeight: '700',
            marginBottom: '1rem'
          }}>
            <Shield size={16} color="#CEF17B" /> Privacy & Security Commitment
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '900', margin: '0 0 1rem 0', letterSpacing: '-0.5px' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: '1.6', margin: 0 }}>
            Last Updated: July 29, 2026 • TurfX Sports Booking Platform
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '900px', margin: '-2rem auto 0 auto', padding: '0 1.5rem' }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '3rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
          border: '1.5px solid #EEF2E6'
        }}>
          {/* Introduction */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#084734', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Eye size={22} color="#084734" /> 1. Introduction
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem' }}>
              At <strong>TurfX</strong>, we take your privacy and data security seriously. This Privacy Policy outlines how TurfX ("we", "our", or "us") collects, uses, discloses, and protects your personal information when you use our website, mobile application, and related sports booking services.
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Data We Collect */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#084734', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileText size={22} color="#084734" /> 2. Information We Collect
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem' }}>
              We collect information to provide better sports booking services, process payments securely, and keep your venue reservations accurate:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem', paddingLeft: '1.5rem' }}>
              <li><strong>Account Information:</strong> Name, email address, phone number, and password when you register.</li>
              <li><strong>Booking Details:</strong> Dates, slot timings, venue choices, total amount paid, and booking status.</li>
              <li><strong>Payment Data:</strong> Payment transaction reference numbers processed via encrypted payment gateways. We do not store raw card credentials or CVV numbers.</li>
              <li><strong>Device & Location Data:</strong> Device model, operating system, and approximate location (when search by location is enabled).</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* How We Use Information */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#084734', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle2 size={22} color="#084734" /> 3. How We Use Your Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.25rem', borderRadius: '16px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800' }}>Slot Booking Confirmation</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748B', lineHeight: '1.5' }}>Processing your reservations and sending QR code passes for court entry.</p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.25rem', borderRadius: '16px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800' }}>Customer Support</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748B', lineHeight: '1.5' }}>Resolving support tickets, cancellations, refunds, and venue inquiries.</p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.25rem', borderRadius: '16px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800' }}>Offers & Reminders</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748B', lineHeight: '1.5' }}>Sending updates regarding live venue discounts and booking reminders.</p>
              </div>
            </div>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Data Security & Sharing */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#084734', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Lock size={22} color="#084734" /> 4. Data Security & Third-Party Sharing
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem' }}>
              We employ SSL/TLS encryption for all data transfers. We do not sell or rent your personal information to third parties. Information is only shared with verified venue owners to validate your slot check-in, or payment service providers to execute transactions securely.
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Contact Section */}
          <section>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#084734', marginBottom: '1rem' }}>
              5. Contact Our Privacy Team
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem' }}>
              If you have any questions, concerns, or data requests regarding this Privacy Policy, please reach out to us:
            </p>
            <div style={{ background: '#084734', color: 'white', borderRadius: '16px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Mail size={18} color="#CEF17B" /> <span>support@metaqode.co.in</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Phone size={18} color="#CEF17B" /> <span>+91 9980998199</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><MapPin size={18} color="#CEF17B" /> <span>MetaQode Technologies Pvt. Ltd., Chitradurga - 577501, Karnataka, India</span></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
